// Why the agent picked this company: criteria scores, what rules it out, why now, the website, news and signals (job
// ads, tenders, regulator rules, annual report) it read, and the people found, any of whom can become the one the
// email goes to.
import { Loader2, UserCheck } from "lucide-react";
import { useDataProvider, useNotify, useRefresh, useTranslate } from "ra-core";
import { useState, type FormEvent, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import type { CrmDataProvider } from "../../providers/types";
import type { FoundContact } from "../leads/findContacts";
import { beforeApproval } from "./preLeadDisplay";
import { writeToCandidate } from "./preLeads";
import type { PreLead, PreLeadMessage } from "./types";
import { useDrawerTask } from "./useDrawerTask";

type News = { title?: string; date?: string; url?: string; source?: string };
const SIGNAL_KINDS = ["job", "tender", "regulation", "report"];

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[6rem_1fr] gap-2 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <div className="min-w-0">{children}</div>
  </div>
);

/** Dated items with their links (http(s) only: the addresses come from web pages) */
const Dated = ({ items }: { items: News[] }) => (
  <ul className="flex flex-col gap-1">
    {items.map((n, i) => (
      <li key={i}>
        {n.date && (
          <span className="mr-1 tabular-nums text-muted-foreground">
            {n.date}
          </span>
        )}
        {n.url && /^https?:\/\//i.test(n.url) ? (
          <a
            href={n.url}
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2"
          >
            {n.title ?? n.url}
          </a>
        ) : (
          n.title
        )}
        {n.source && (
          <span className="text-muted-foreground"> ({n.source})</span>
        )}
      </li>
    ))}
  </ul>
);

const fullName = (p: {
  first_name?: string | null;
  last_name?: string | null;
}) => [p.first_name, p.last_name].filter(Boolean).join(" ");

const CONTACT_FIELDS = [
  ["contact_first_name", "text"],
  ["contact_last_name", "text"],
  ["contact_title", "text"],
  ["contact_email", "email"],
] as const;
type ContactField = (typeof CONTACT_FIELDS)[number][0];

/** The User names the person to write to (a pre-lead the agent found nobody for, or a better person): the four contact
 * fields go on the row; Keep then drafts to that person (keepPreLead reads the row's own contact first). */
const ContactForm = ({
  preLead,
  onDone,
}: {
  preLead: PreLead;
  onDone: () => void;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState<Record<ContactField, string>>(
    () =>
      Object.fromEntries(
        CONTACT_FIELDS.map(([f]) => [f, preLead[f] ?? ""]),
      ) as Record<ContactField, string>,
  );
  const t = (key: string) => translate(`resources.pre_leads.drawer.${key}`);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const data = Object.fromEntries(
      CONTACT_FIELDS.map(([f]) => [f, values[f].trim() || null]),
    ) as Partial<PreLead>;
    if (!data.contact_email) return;
    setSaving(true);
    try {
      await dataProvider.update<PreLead>("pre_leads", {
        id: preLead.id,
        data,
        previousData: preLead,
      });
      notify(t("contact_saved"), { type: "success" });
      refresh();
      onDone();
    } catch (error) {
      notify((error as Error).message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        {CONTACT_FIELDS.map(([field, type]) => (
          <Input
            key={field}
            type={type}
            value={values[field]}
            required={field === "contact_email"}
            placeholder={t(`contact_fields.${field}`)}
            aria-label={t(`contact_fields.${field}`)}
            onChange={(e) => setValues({ ...values, [field]: e.target.value })}
          />
        ))}
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? <Loader2 className="animate-spin" /> : <UserCheck />}
          {t("save_contact")}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          {translate("ra.action.cancel")}
        </Button>
      </div>
    </form>
  );
};

export const EvidenceTab = ({
  preLead,
  draft,
  signature,
}: {
  preLead: PreLead;
  /** the touch-1 email, re-greeted when another person is picked */
  draft: PreLeadMessage | undefined;
  signature: string;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const { running, run } = useDrawerTask();
  const t = (key: string, args?: Record<string, unknown>) =>
    translate(`resources.pre_leads.drawer.${key}`, args);
  const evidence = preLead.evidence ?? {};
  const ruledOut = Array.isArray(evidence.ruled_out)
    ? (evidence.ruled_out as string[])
    : [];
  const news = Array.isArray(evidence.news) ? (evidence.news as News[]) : [];
  const signals = Array.isArray(evidence.signals)
    ? (evidence.signals as (News & { kind?: string })[])
    : [];
  const siteSummary =
    typeof evidence.site_summary === "string" ? evidence.site_summary : null;
  const people = (preLead.candidates ?? []) as FoundContact[];
  const writingTo = preLead.contact_email?.toLowerCase();
  const open = beforeApproval(preLead);
  const [editingContact, setEditingContact] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      <Row label={t("criteria")}>
        {preLead.criteria?.length ? (
          <ul className="flex flex-col gap-1">
            {preLead.criteria.map((c) => (
              <li key={c.name} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate" title={c.name}>
                  {c.name}
                </span>
                <span
                  className="h-1.5 w-10 shrink-0 rounded-full bg-muted"
                  aria-hidden
                >
                  <span
                    className="block h-full rounded-full bg-primary"
                    style={{ width: `${(Math.min(5, c.score) / 5) * 100}%` }}
                  />
                </span>
                <span className="w-7 shrink-0 text-right tabular-nums text-muted-foreground">
                  {c.score}/5
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-muted-foreground">{t("none")}</span>
        )}
      </Row>
      <Row label={t("ruled_out")}>
        {ruledOut.length ? ruledOut.join(" · ") : t("none")}
      </Row>
      {preLead.why && <Row label={t("why")}>{preLead.why}</Row>}
      <Row label={t("why_now")}>
        {preLead.why_now ?? (
          <span className="text-muted-foreground">
            {translate("resources.pre_leads.none_found")}
          </span>
        )}
      </Row>
      <Row label={t("write_to")}>
        {preLead.contact_email ? (
          <>
            {fullName({
              first_name: preLead.contact_first_name,
              last_name: preLead.contact_last_name,
            }) || preLead.contact_email}
            {preLead.contact_title && `, ${preLead.contact_title}`}
            {preLead.mailbox && (
              <span className="block text-xs text-muted-foreground">
                {preLead.contact_email} ·{" "}
                {t("from", { mailbox: preLead.mailbox })}
              </span>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">{t("nobody")}</span>
        )}
        {open && !editingContact && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto px-0"
            onClick={() => setEditingContact(true)}
          >
            {t(preLead.contact_email ? "change_contact" : "add_contact")}
          </Button>
        )}
        {open && editingContact && (
          <ContactForm
            preLead={preLead}
            onDone={() => setEditingContact(false)}
          />
        )}
      </Row>
      {siteSummary && <Row label={t("site")}>{siteSummary}</Row>}
      {news.length > 0 && (
        <Row label={t("news")}>
          <Dated items={news} />
        </Row>
      )}
      {SIGNAL_KINDS.map((kind) => {
        const items = signals.filter((s) => s.kind === kind);
        return (
          items.length > 0 && (
            <Row key={kind} label={t(`signals.${kind}`)}>
              <Dated items={items} />
            </Row>
          )
        );
      })}
      {people.length > 0 && (
        <div className="flex flex-col gap-2">
          <h4 className="text-sm font-semibold">{t("people")}</h4>
          {people.map((person) => {
            const current = person.email.toLowerCase() === writingTo;
            return (
              <div
                key={person.email}
                className="flex items-center justify-between gap-2 rounded-md border p-2 text-sm"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">
                    {fullName(person) || person.email}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {[person.title, person.email].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {current ? (
                  <UserCheck
                    className="size-4 shrink-0 text-primary"
                    aria-label={t("write_to")}
                  />
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={running != null || !open}
                    title={open ? undefined : t("locked")}
                    onClick={() =>
                      run(person.email, async () => {
                        await writeToCandidate(
                          dataProvider,
                          preLead,
                          person,
                          draft,
                          signature,
                        );
                        notify("resources.pre_leads.drawer.notify.writing_to", {
                          messageArgs: {
                            name: fullName(person) || person.email,
                          },
                        });
                      })
                    }
                  >
                    {running === person.email && (
                      <Loader2 className="animate-spin" />
                    )}
                    {t("write_to_person")}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
